import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Priority } from "@zero/types/enum";
import type { TodoItem as TodoItemType } from "@zero/types/todo";
import { cn } from "@zero/utils";
import { CalendarDays, Check, GripVertical, Trash2 } from "lucide-react";
import { motion } from "motion/react";
import { formatDueDate, isOverdue } from "../utils/date";
import { deriveTodoActionRights, isTodoCompleted } from "../utils/todoRights";

interface TodoItemProps {
  todo: TodoItemType;
  disabled?: boolean;
  selected?: boolean;
  onSelect?: (id: string) => void;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onOpen?: (id: string) => void;
}

const priorityColors: Record<Priority, string> = {
  [Priority.Low]: "bg-success",
  [Priority.Medium]: "bg-warning",
  [Priority.High]: "bg-orange-500",
  [Priority.Urgent]: "bg-destructive",
};

const priorityBorders: Record<Priority, string> = {
  [Priority.Low]: "border-l-success",
  [Priority.Medium]: "border-l-warning",
  [Priority.High]: "border-l-orange-500",
  [Priority.Urgent]: "border-l-destructive",
};

export function TodoItem({
  todo,
  disabled = false,
  selected = false,
  onSelect,
  onToggle,
  onDelete,
  onOpen,
}: TodoItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: todo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const rights = deriveTodoActionRights(todo.relationship);
  const completed = isTodoCompleted(todo);
  const overdue = isOverdue(todo.dueDate, completed);
  const statusDisabled = disabled || completed || todo.status === "archived" || !rights.canTransitionStatus;

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className={cn(
        "group flex items-center gap-3 p-3 bg-card rounded-md shadow-sm border-l-4 transition-all hover:shadow-md",
        priorityBorders[todo.priority],
        isDragging && "opacity-50",
        completed && "opacity-60",
        selected && "ring-1 ring-primary/40",
      )}
    >
      {/* Selection */}
      <input
        type="checkbox"
        checked={selected}
        disabled={disabled || todo.relationship === "viewer"}
        onChange={() => onSelect?.(todo.id)}
        onClick={(event) => event.stopPropagation()}
        aria-label={`选择 ${todo.title}`}
        className="h-4 w-4 rounded border-input text-primary focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      />

      {/* Drag Handle */}
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        aria-label={`拖动 ${todo.title}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={18} />
      </button>

      {/* Priority Indicator */}
      <div className={cn("w-2 h-2 rounded-full", priorityColors[todo.priority])} />

      {/* Checkbox */}
      <button
        type="button"
        disabled={statusDisabled}
        onClick={() => onToggle(todo.id)}
        aria-label={`标记 ${todo.title} 为完成`}
        className={cn(
          "flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all",
          completed ? "bg-primary border-primary text-primary-foreground" : "border-input hover:border-primary",
          statusDisabled && "cursor-not-allowed opacity-60",
        )}
      >
        {completed && <Check size={12} />}
      </button>

      {/* Content */}
      <button type="button" onClick={() => onOpen?.(todo.id)} className="flex-1 min-w-0 text-left">
        <p
          className={cn(
            "text-sm font-medium text-foreground truncate",
            completed && "line-through text-muted-foreground",
          )}
        >
          {todo.title}
        </p>
        {todo.description && (
          <p className={cn("text-xs text-muted-foreground truncate", completed && "line-through")}>
            {todo.description}
          </p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <span className="inline-flex items-center px-2 py-0.5 bg-muted text-xs font-medium text-muted-foreground rounded-md">
            {todo.category}
          </span>
          {todo.assigneeId && (
            <span className="inline-flex items-center rounded-md bg-blue-500/10 px-2 py-0.5 text-xs text-blue-600">
              指派
            </span>
          )}
          {todo.relationship !== "viewer" && (
            <span className="inline-flex items-center rounded-md bg-secondary px-2 py-0.5 text-xs text-secondary-foreground">
              {todo.relationship === "owner" ? "Mine" : "Assigned to me"}
            </span>
          )}
          {todo.dueDate && (
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium",
                overdue ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground",
              )}
            >
              <CalendarDays size={12} />
              {formatDueDate(todo.dueDate)}
            </span>
          )}
          {todo.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded-md bg-secondary px-2 py-0.5 text-xs text-secondary-foreground"
            >
              {tag}
            </span>
          ))}
        </div>
      </button>

      {/* Delete Button */}
      <button
        type="button"
        disabled={disabled || !rights.canDelete}
        onClick={() => onDelete(todo.id)}
        aria-label={`删除 ${todo.title}`}
        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Trash2 size={16} />
      </button>
    </motion.div>
  );
}
